import cloneDeep from 'lodash.clonedeep';
import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './sipSetting.api';
import { getSelectedSipSetting } from './sipSetting.selectors';
import {
  GET_SIP_SETTING,
  UPDATE_SIP_SETTING,
  DELETE_SIP_SETTING,
  GO_TO_SIP_SETTING_EDIT,
  GO_TO_SIP_SETTING_VIEW,
  GO_TO_SIP_SETTING_CREATE,
  SET_SIP_SETTING_LOADING,
  CREATE_SIP_SETTING,
  UpdateSipSettingThunkAction,
  DeleteSipSettingThunkAction,
  SetSipSettingLoadingAction,
  SipSettingActionTypes,
  SipSetting,
  CreateSipSettingThunkAction,
  SipSettingErrors,
  UPDATE_SIP_ERRORS,
} from './sipSetting.types';
import { SIP_SETTING, SYSTEM, SYSTEM_CONFIGURATION_ROUTE } from '../../constants';
import { adaptErrorResponsesMessages } from './sipSetting.adapter';

export function goToSipSettingCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SIP_SETTING_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${SIP_SETTING}/create`);
  };
}

export function goToSipSettingEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SIP_SETTING_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${SIP_SETTING}/edit`);
  };
}

export function goToSipSettingView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SIP_SETTING_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${SIP_SETTING}`);
  };
}

export function setSipSettingLoadingAction(
  isLoading: boolean,
): SetSipSettingLoadingAction {
  return {
    type: SET_SIP_SETTING_LOADING,
    payload: { isLoading },
  };
}

export function getSipSetting(
  deliveryFunctionId: string
): SipSettingActionTypes {
  return {
    type: GET_SIP_SETTING,
    payload: api.getSipSetting(deliveryFunctionId),
  };
}

export function updateSipSetting(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<SipSetting>,
  history: History,
): UpdateSipSettingThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      const response = await dispatch({
        type: UPDATE_SIP_SETTING,
        payload: api.updateSipSetting(deliveryFunctionId, id, body),
      }) as any;

      dispatch(goToSipSettingView(history));
      showSuccessMessage(i18n.t('sipSetting.update.success'), 5000);

      dispatch(updateSipSettingErrors(adaptErrorResponsesMessages(response, body.data)));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('sipSetting.update.failed', { error: errorMessage }));
    }
  };
}

export function createSipSetting(
  deliveryFunctionId: string,
  body: WithAuditDetails<SipSetting>,
  history: History,
): CreateSipSettingThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      const response = await dispatch({
        type: CREATE_SIP_SETTING,
        payload: api.createSipSetting(deliveryFunctionId, body),
      });

      dispatch(goToSipSettingView(history));

      showSuccessMessage(i18n.t('sipSetting.create.success'), 5000);
      dispatch(updateSipSettingErrors(adaptErrorResponsesMessages(response, body.data)));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('sipSetting.create.failed', { error: errorMessage }));
    }
  };
}

export function deleteSipSetting(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<SipSetting>,
): DeleteSipSettingThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedSipSetting] = getSelectedSipSetting(getState());

    try {
      await dispatch({
        type: DELETE_SIP_SETTING,
        payload: api.deleteSipSetting(deliveryFunctionId, id, cloneDeep(body)),
      });

      dispatch(getSipSetting(deliveryFunctionId));
      showSuccessMessage(i18n.t('sipSetting.delete.success'));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('sipSetting.delete.failed', { error: errorMessage }));
    }
  };
}

export function updateSipSettingErrors(sipSettingErrors: SipSettingErrors): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: UPDATE_SIP_ERRORS,
      payload: sipSettingErrors
    });
  };
}
